import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const TYPES = [
  { v: "ptw", l: "Permit To Work" },
  { v: "incident", l: "Incident" },
  { v: "near_miss", l: "Near Miss" },
  { v: "toolbox", l: "Toolbox Talk" },
  { v: "audit", l: "Audit" },
  { v: "drill", l: "Emergency Drill" },
];

export default function Safety() {
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ project_id: "", type: "ptw", sub_type: "", date: today, description: "", severity: "low", status: "open" });

  useEffect(() => {
    api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setForm((f) => ({ ...f, project_id: r.data[0].id })); });
    load();
  }, []);
  const load = () => api.get("/safety", { params: typeFilter !== "all" ? { type: typeFilter } : {} }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [typeFilter]);

  const submit = async () => {
    try { await api.post("/safety", form); toast.success("Safety record saved"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filt = list.filter((s) => !q || `${s.description} ${s.type}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppLayout title="Safety & HSE" subtitle="PTW · Incidents · Near misses · Toolbox · Audits · Drills">
      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="New Record"
        addTestId="safety-add-button" searchTestId="safety-search-input"
        filters={
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44" data-testid="safety-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Sub-type</TableHead>
              <TableHead>Description</TableHead><TableHead>Severity</TableHead>
              <TableHead>Status</TableHead><TableHead>Reported By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filt.map((s) => (
              <TableRow key={s.id} data-testid={`safety-row-${s.id}`}>
                <TableCell className="font-mono text-xs">{s.date}</TableCell>
                <TableCell><StatusBadge status={s.type === "incident" ? "critical" : "active"} /> <span className="ml-1 text-xs">{TYPES.find((t) => t.v === s.type)?.l}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{s.sub_type || "—"}</TableCell>
                <TableCell className="max-w-md truncate">{s.description}</TableCell>
                <TableCell>{s.severity ? <StatusBadge status={s.severity} /> : "—"}</TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
                <TableCell className="text-xs text-slate-500">{s.reported_by}</TableCell>
              </TableRow>
            ))}
            {!filt.length && <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">No safety records</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="safety-create-dialog">
          <DialogHeader><DialogTitle>New Safety Record</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-cap">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="safety-type-input"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Sub-type</Label><Input data-testid="safety-sub-input" value={form.sub_type} onChange={(e) => setForm({ ...form, sub_type: e.target.value })} placeholder="hot_work / height …" /></div>
            <div><Label className="label-cap">Date</Label><Input type="date" data-testid="safety-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="safety-severity-input"><SelectValue /></SelectTrigger>
                <SelectContent>{["low", "medium", "high"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="label-cap">Description</Label><Textarea data-testid="safety-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="col-span-2">
              <Label className="label-cap">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="safety-project-input"><SelectValue /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="safety-submit-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
